import { Drawer, PaperProps } from '@mui/material'
import React, {
  ReactElement,
  ReactNode,
  RefObject,
  useEffect,
  useRef,
} from 'react'
import { I18n, useClickOutsideDetector } from 'common'
import styled from 'styled-components'
import CloseIcon from '@mui/icons-material/Close'
import { breakpoints } from '../../theme/breakpoints'
import { zIndex } from '../../theme/zIndex'
import { defaultTheme } from '../../theme/index'
import { isMobileOnly } from 'react-device-detect'

interface BaseDrawerProps {
  open: boolean
  title: string | ReactElement
  header?: ReactNode
  body: ReactNode
  footer?: ReactNode
  paddingX?: string
  paddingY?: string
  showHeaderDivider?: boolean
  showFooterDivider?: boolean
  width?: string
  minWidth?: string
  onClose: () => void
  height?: string
  clickOutsideCustomRefs?: Array<React.RefObject<HTMLElement>>
  paperProps?: Partial<PaperProps>
  getPrintRef?: (ref: RefObject<HTMLDivElement>) => void
  scrollable?: boolean
  manageScrollLock?: boolean
}

const BaseDrawer = (props: BaseDrawerProps): ReactElement => {
  const ref = useRef<HTMLDivElement>(null)

  const {
    open,
    title,
    header,
    body,
    footer,
    paddingX = '2rem',
    paddingY = '1rem',
    width = 'auto',
    minWidth = window.innerWidth < breakpoints.mobileLarge ? '100%' : '500px',
    showHeaderDivider = false,
    showFooterDivider = false,
    onClose,
    height = '100%',
    clickOutsideCustomRefs = [],
    paperProps = {
      sx: {
        zIndex: zIndex.modal,
      },
    },
    getPrintRef,
    scrollable = false,
    manageScrollLock = false,
  } = props

  useClickOutsideDetector([ref, ...clickOutsideCustomRefs], open, onClose)

  useEffect(() => {
    if (getPrintRef) {
      getPrintRef(ref)
    }
  }, [getPrintRef])

  /**
   * MPV3-8392
   * Manually manages body scroll lock for drawers that may be unmounted before MUI's
   * internal ModalManager can restore the scroll state. When `manageScrollLock` is true,
   * MUI's built-in scroll lock is disabled (via disableScrollLock prop on Drawer), and
   * this effect takes over: hiding body overflow when the drawer opens, and restoring it
   * when the drawer closes or the component unmounts. This prevents the page from losing
   * its scroll capability when drawers are removed abruptly (e.g., via nice-modal's
   * modal.remove() or conditional rendering unmount).
   */
  useEffect(() => {
    if (!manageScrollLock) return undefined
    if (open) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.removeProperty('overflow')
      document.body.style.removeProperty('padding-right')
    }
    return () => {
      document.body.style.removeProperty('overflow')
      document.body.style.removeProperty('padding-right')
    }
  }, [open, manageScrollLock])

  return (
    <Drawer
      open={open}
      anchor={'right'}
      PaperProps={paperProps}
      disableScrollLock={manageScrollLock}
    >
      <Wrapper
        width={width}
        minWidth={minWidth}
        ref={ref}
        height={height}
        scrollable={scrollable}
      >
        <Title paddingX={paddingX} paddingY={paddingY}>
          <TitleHeader>
            {title && typeof title === 'string' && (
              <>
                <I18n name={title} />
                <StyledCloseIcon onClick={onClose} />
              </>
            )}
            {title && typeof title === 'object' && title}
          </TitleHeader>
          {header}
        </Title>
        {showHeaderDivider && <Divider />}

        <BodyWrapper
          paddingX={paddingX}
          paddingY={paddingY}
          scrollable={scrollable}
        >
          {body}
        </BodyWrapper>

        <FooterWrapper>
          {showFooterDivider && <Divider />}
          <PaddingWrapper paddingX={paddingX} paddingY={paddingY}>
            {footer}
          </PaddingWrapper>
        </FooterWrapper>
      </Wrapper>
    </Drawer>
  )
}

export default BaseDrawer

interface WrapperProps {
  width: string
  minWidth: string
  height: string
  scrollable: boolean
}
const Wrapper = styled.div<WrapperProps>`
  display: flex;
  flex-direction: column;
  width: ${(props) => props.width};
  min-width: ${(props) => props.minWidth};
  max-width: ${(props) => (props.width !== 'auto' ? props.width : 'none')};
  height: ${(props) => props.height};
  overflow: ${(props) => (props.scrollable ? 'auto' : 'visible')};
`

const Divider = styled.div`
  width: 100%;
  border-bottom: 2px solid #d3d2cf;
`

const TitleHeader = styled.div`
  display: flex;
  justify-content: space-between;
  font-weight: bold;
  align-items: center;
`

interface PaddingProps {
  paddingX: string
  paddingY: string
}

const PaddingWrapper = styled.div<PaddingProps>`
  padding-left: ${(props) => props.paddingX};
  padding-right: ${(props) => props.paddingX};
  padding-top: ${(props) => props.paddingY};
  padding-bottom: ${(props) => props.paddingY};
`

const Title = styled(PaddingWrapper)`
  font-family: Oswald, serif;
  font-style: normal;
  font-size: ${() => (isMobileOnly ? '20px' : '28px')};
  line-height: 48px;
  /* identical to box height, or 171% */
  /* Almost Black */
  color: ${defaultTheme.colors.almostBlack};
`

interface BodyWrapperProps {
  scrollable?: boolean
}

const BodyWrapper = styled(PaddingWrapper)<BodyWrapperProps>`
  height: auto;
  ${(props) =>
    !props.scrollable &&
    `
    @media not print {
      overflow-y: auto;
    }
  `}
`

const StyledCloseIcon = styled(CloseIcon)`
  cursor: pointer;
`

const FooterWrapper = styled.div`
  margin-top: auto;
  @media print {
    display: none;
  }
`
